import 'core-js';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'regenerator-runtime/runtime';
import fetch from 'isomorphic-fetch';
import { getValidAccessToken, refreshAccessToken, endSession } from './session';

const buildHeaders = async (chain) => {
    const headers = { ...chain.headers };

    if (!chain.authRequired) {
        return headers;
    }

    // An explicit token is used as given: the caller has a reason for that one
    // (a token from IndexedDB, a test) and refreshing it is not this layer's
    // business.
    const token = chain.explicitToken || await getValidAccessToken();

    if (token) {
        headers.Authorization = 'Bearer ' + token;
    }

    return headers;
}

// [Builder pattern]
class Requester {
    constructor() {
        this.definedHeaders = {}
        this.authRequired = false;
        this.explicitToken = null;
    }

    /**
     * Marks the request as authenticated, optionally with a caller-supplied token.
     *
     * The token is deliberately not read here. Access tokens live 15 minutes, and
     * this builder is a singleton whose chain can be assembled well before the
     * request goes out, so a token captured at this point may already be stale by
     * the time it is sent. Resolution — and renewal, if needed — happens in
     * `buildHeaders`, immediately before the fetch.
     */
    authNeed(token = null) {
        this.authRequired = true;
        this.explicitToken = token;
        return this;
    }

    contentType(contenttype = 'application/json') {
        delete this.definedHeaders['Content-Type'];
        if (contenttype) {
            this.definedHeaders['Content-Type'] = contenttype;
        }
        return this;
    }

    /**
     * Takes the state the chain accumulated and resets the builder.
     *
     * This class is a singleton, so anything left set here leaks into the next
     * request made anywhere in the app. `.authNeed()` leaking is the dangerous
     * one: the login page calls `unsubscribeNotification()` on mount, and every
     * later call — including the unauthenticated POST to /o/token — would inherit
     * `authRequired` and treat its 401 as an expired session.
     */
    consumeChain() {
        const chain = {
            headers: { ...this.definedHeaders },
            authRequired: this.authRequired,
            explicitToken: this.explicitToken,
        };

        this.authRequired = false;
        this.explicitToken = null;
        delete this.definedHeaders['Content-Type'];

        return chain;
    }

    /**
     * The single place a request actually leaves the app, so the retry-on-401 below
     * covers every verb.
     */
    async request(method, url, { body = null, isFormData = false } = {}) {
        // Read and clear the builder state before the first `await`, so a request
        // started while this one is resolving its token cannot pick up this one's
        // chain.
        const chain = this.consumeChain();
        const headers = await buildHeaders(chain);
        const requestDetails = {
            method,
            headers,
            // Needed on the login call: the refresh cookie arrives as a Set-Cookie on
            // a cross-origin response, and the browser only stores that in credentials
            // mode. Elsewhere it costs nothing — the cookie is scoped to /auth, so it
            // is not attached to any of these requests.
            credentials: 'include',
        };

        if (body !== null) {
            requestDetails.body = isFormData === true ? body : JSON.stringify(body);
        }

        let customRequest;

        try {
            customRequest = await fetch(url, requestDetails);
        }
        catch (err) {
            return err;
        }

        // A 401 on an authenticated call is the expected end of every access token,
        // not an error worth surfacing. One refresh, one retry: if the refresh
        // produced a token the original token had simply aged out, and the retry is
        // the request the caller asked for. Bounded to a single attempt, so a route
        // that 401s for some other reason cannot loop.
        if (customRequest.status === 401 && chain.authRequired && !chain.explicitToken) {
            const refreshedToken = await refreshAccessToken();

            if (refreshedToken) {
                try {
                    customRequest = await fetch(url, {
                        ...requestDetails,
                        headers: { ...headers, Authorization: 'Bearer ' + refreshedToken },
                    });
                }
                catch (err) {
                    return err;
                }
            }
        }

        // Still 401 after that: there is no session left to renew. Only for a call
        // that actually presented a credential — an open endpoint answering 401 says
        // nothing about the session, and ending it there would sign the user out on
        // a failed login.
        if (customRequest.status === 401 && chain.authRequired) {
            endSession();
        }

        let result;

        try {
            result = await customRequest.json();
        }
        catch (err) {
            return err;
        }

        // Some of the older endpoints report the status in the body instead of on the
        // response, so both forms are checked — under the same rule as above.
        if (result?.status === 401 && chain.authRequired) {
            endSession();
        }

        return result;
    }

    // HTTP methods:
    async get(url) {
        return this.request('GET', url);
    }

    async post(url, data = {}, isFormData = false) {
        return this.request('POST', url, { body: data, isFormData });
    }

    async put(url, data = {}, isFormData = false) {
        return this.request('PUT', url, { body: data, isFormData });
    }

    async delete(url, data = null) {
        return this.request('DELETE', url, { body: data });
    }
}

export default new Requester();
